import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";

import { UseCompanyColors, UseCompanyWorkspaces, UseSelectedWorkspace } from "../../../hooks/company";
import useLeaderboardData, { formatAmount, closedRateKeyOf } from "./data";
import { resolveCardWindow } from "../../../helpers/cardWindow";
import { periodWindowLabelTitle } from "../../../helpers/periodWindowLabel";
import "../../shared/Loading/skeleton.css";
import "./table.css";

/**
 * The leaderboard, as a table.
 *
 * It ALWAYS ranks people — a leaderboard is players, never teams. The columns
 * are whatever the chosen workspaces track, read live (see ./data.js), so this
 * one component serves a lean board on a workspace dashboard and a fat one on
 * an Analytics dashboard or a TV without a second kind of leaderboard existing.
 *
 * Every part is a switch in the editor's Appearance tab; `settings.hide` is the
 * same shape the rest of the widgets use, so nothing here is bespoke plumbing.
 */

const initialsOf = (p) =>
  `${(p.firstName || p.name || "?")[0] || ""}${(p.lastName || "")[0] || ""}`.toUpperCase() || "?";

const nameOf = (p) =>
  [p.firstName, p.lastName].filter(Boolean).join(" ") || p.name || p.email || "Player";

/* A stable colour per player so the initials chip isn't a grey blob. */
const AV_COLORS = ["#1B598E", "#7A5AF8", "#0E9384", "#B54708", "#D92D20", "#475467", "#6941C6"];
const avColor = (id) => {
  const s = String(id || "");
  let n = 0;
  for (let i = 0; i < s.length; i += 1) n = (n + s.charCodeAt(i)) % AV_COLORS.length;
  return AV_COLORS[n];
};

/* Up or down against last period. Nobody last period reads as "new", never as
   a fall to last place. */
const movement = (was, now) => {
  if (was == null) return <span className="lbt-mv new">new</span>;
  if (was === now) return <span className="lbt-mv same">–</span>;
  if (was > now) return <span className="lbt-mv up">{`▲${was - now}`}</span>;
  return <span className="lbt-mv dn">{`▼${now - was}`}</span>;
};

/* A player row draws initials unless there is a real photo. Several records
   carry an empty or dead URL, which rendered the browser's broken-image glyph
   — so the src is validated, and onError swaps back to initials at runtime. */
const Avatar = ({ row }) => {
  const [broken, setBroken] = useState(false);
  const src = typeof row.image === "string" && /^(https?:|data:|\/)/.test(row.image.trim())
    ? row.image.trim()
    : "";
  if (!src || broken) {
    return <span className="lbt-av" style={{ background: avColor(row.userId) }}>{initialsOf(row)}</span>;
  }
  return <img className="lbt-av" src={src} alt="" onError={() => setBroken(true)} />;
};
Avatar.propTypes = { row: PropTypes.shape({ image: PropTypes.string, userId: PropTypes.string }).isRequired };

const rankClass = (i, medals) => {
  if (!medals) return "";
  if (i === 0) return " g";
  if (i === 1) return " s";
  if (i === 2) return " b";
  return "";
};

const LeaderboardTable = ({ settings = {}, periodIdx = 3, boardWindow = null, boardCustom = null, title, meUserId = null }) => {
  const selected = UseSelectedWorkspace();
  const allWorkspacesRaw = UseCompanyWorkspaces(true);
  const { primaryColor } = UseCompanyColors();

  const hide = settings.hide || {};
  const shows = (key) => !hide[key];

  /* No workspaces picked = the one the board is already on. A card dropped on a
     workspace dashboard needs no setup to be correct. */
  const workspaceIds = useMemo(() => {
    const picked = (settings.workspaceIds || []).filter(Boolean).map(String);
    if (picked.length) return picked;
    return selected?._id ? [String(selected._id)] : [];
  }, [settings.workspaceIds, selected?._id]);

  const { window: bucket, custom: resolvedCustom } = resolveCardWindow(settings, {
    window: boardWindow != null ? boardWindow : periodIdx,
    custom: boardCustom,
  });
  const custom = bucket === "custom" ? (resolvedCustom || boardCustom) : null;
  const { columns, rows, yetiPts, loading, refreshing } = useLeaderboardData(
    workspaceIds,
    bucket,
    settings.metricPlan || null,
    custom,
  );

  const wsName = useMemo(() => {
    const map = {};
    (allWorkspacesRaw || []).forEach((w) => { map[String(w._id)] = w.name; });
    return map;
  }, [allWorkspacesRaw]);

  const multi = workspaceIds.length > 1;
  const showTeam = shows("team") && multi;
  const showPills = shows("pills") && multi;
  const showMetrics = shows("metrics");

  /* Rank by whatever Setup says, falling back on its own when a workspace
     change retires the metric it pointed at — never dangle at a dead column. */
  const rankKeys = useMemo(
    () => [...columns.map((c) => c.key), "pts"],
    [columns],
  );
  const wanted = settings.rankBy || "pts";
  const [sortKey, setSortKey] = useState(null);
  let activeKey = "pts";
  if (rankKeys.includes(sortKey)) activeKey = sortKey;
  else if (rankKeys.includes(wanted)) activeKey = wanted;
  const dir = settings.rankOrder === "asc" ? 1 : -1;
  /* Closed Rate is the default tie-breaker when Setup hasn't picked one. */
  const closedKey = closedRateKeyOf(columns);
  const wantedTie = settings.tieBreak || closedKey || null;
  let tieKey = null;
  if (wantedTie && rankKeys.includes(wantedTie) && wantedTie !== activeKey) {
    tieKey = wantedTie;
  } else if (closedKey && closedKey !== activeKey && rankKeys.includes(closedKey)) {
    tieKey = closedKey;
  }
  /* Tie-break always prefers the higher Closed Rate (or chosen metric), even
     when the primary rank is lowest-first. */
  const tieDir = -1;

  const valueOf = (row, key) => (key === "pts" ? row.pts : row.values?.[key]) || 0;

  /* 4-of-5 style gate: need N metrics beating the Yeti's period pace.
     Default ON for cross-workspace boards; Setup can turn it off. */
  const qualifyOn = settings.yetiQualify !== false && columns.length >= 2;
  const qualifyNeed = Math.min(
    Math.max(1, Number(settings.yetiQualifyNeed) || 4),
    columns.length || 1,
  );
  const isQualified = (row) => !qualifyOn || (Number(row.beatCount) || 0) >= qualifyNeed;

  /* On a wall there is no scrolling, so a row that does not fit is a player
     who silently disappears — and the container clipped them mid-row, which
     read as a rendering fault. Measure what fits and show whole rows only. */
  const scrollRef = useRef(null);
  const [roomPx, setRoomPx] = useState(0);
  const [onWall, setOnWall] = useState(false);

  useLayoutEffect(() => {
    setOnWall(!!scrollRef.current?.closest(".tv-board"));
  }, []);

  useEffect(() => {
    const host = scrollRef.current;
    if (!host || !onWall) return undefined;
    const measure = () => {
      const head = host.querySelector("thead");
      const row = host.querySelector("tbody tr");
      if (!row) return;
      /* offsetHeight, NOT getBoundingClientRect: the TV scales the whole board,
         so the rect is post-transform pixels while clientHeight is layout
         pixels. Mixing the two made rows measure shorter than they are, so more
         were admitted than fit — and slicing one shrank the table, which
         re-measured and admitted another. Both sides must be layout space. */
      const rowH = row.offsetHeight;
      const headH = head ? head.offsetHeight : 0;
      if (!rowH) return;
      /* A hair of tolerance: sub-pixel row heights otherwise round UP into a
         row that then gets sliced by the container. */
      const room = host.clientHeight - headH - 1;
      // Height available for rows — divided by the count we're showing so exactly
      // that many fill the card (compressed if need be). rowH is only read to
      // confirm a row exists before measuring.
      setRoomPx(Math.max(0, room));
    };
    measure();
    /* The TV scales the whole board after mount, which changes every size in
       here — so re-measure once the scale has landed, and keep watching BOTH
       the container and the table (the container alone never resizes again). */
    const raf = requestAnimationFrame(measure);
    const settle = setTimeout(measure, 400);
    const ro = new ResizeObserver(measure);
    ro.observe(host);
    const table = host.querySelector("table");
    if (table) ro.observe(table);
    return () => {
      cancelAnimationFrame(raf);
      clearTimeout(settle);
      ro.disconnect();
    };
  }, [onWall, columns.length, rows.length]);

  const [filter, setFilter] = useState("all");
  const activeFilter = filter !== "all" && workspaceIds.includes(filter) ? filter : "all";

  const ranked = useMemo(() => {
    let pool = rows;
    /* "Only people with activity" keeps a new hire off a wall on day one. */
    if (settings.whoAppears === "active") pool = pool.filter((r) => valueOf(r, activeKey) > 0);
    return [...pool].sort((a, b) => {
      /* Qualified players (enough metrics beating Yeti) always rank above the rest. */
      const aq = isQualified(a) ? 1 : 0;
      const bq = isQualified(b) ? 1 : 0;
      if (aq !== bq) return bq - aq;
      const d = (valueOf(a, activeKey) - valueOf(b, activeKey)) * dir;
      if (d !== 0) return d;
      return tieKey ? (valueOf(a, tieKey) - valueOf(b, tieKey)) * tieDir : 0;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, activeKey, dir, tieKey, tieDir, settings.whoAppears, qualifyOn, qualifyNeed]);

  const asked = Number(settings.rows) || 10;

  const inFilter = useMemo(
    () => (activeFilter === "all"
      ? ranked
      : ranked.filter((r) => String(r.wsId) === activeFilter)),
    [ranked, activeFilter],
  );

  /* On a TV wall a big roster can't all fit at once, so rather than dropping
     everyone past the cut we ROTATE: a few names on screen, advancing to the
     next batch on a timer, so the whole team gets airtime. Page size and
     interval are set in the leaderboard editor (default 5 names every 15s);
     "Show the top names only" turns rotation off. Off-wall always keeps the
     asked-for count. pageSize never exceeds what actually fits. */
  const wantSize = Number(settings.tvRotateSize) || 5;
  // Show exactly the requested count on the wall (rows are compressed by CSS to
  // fit — see .lbt.rotating), so a short card still shows all five, not three.
  const pageSize = Math.max(1, wantSize);
  const rotating = onWall && settings.tvRotate !== "topfit" && inFilter.length > pageSize;
  const rotateMs = Math.max(4, Number(settings.tvRotateSecs) || 15) * 1000;
  const pageCount = rotating ? Math.ceil(inFilter.length / pageSize) : 1;
  const [page, setPage] = useState(0);
  useEffect(() => { setPage(0); }, [activeFilter, pageCount]);
  useEffect(() => {
    if (!rotating) return undefined;
    const id = setInterval(() => setPage((p) => (p + 1) % pageCount), rotateMs);
    return () => clearInterval(id);
  }, [rotating, pageCount, rotateMs]);

  /* On the wall we ALWAYS show the configured count (rows are compressed by CSS
     to fit — see .lbt.tvfit), never clamped down to whatever naturally fits — so
     choosing 7 shows 7, not "the four that happen to fit". Off the wall keeps the
     asked-for count with normal scrolling. */
  const visible = useMemo(() => {
    if (rotating) {
      const p = page % pageCount;
      return inFilter.slice(p * pageSize, p * pageSize + pageSize);
    }
    if (onWall) return inFilter.slice(0, pageSize);
    return inFilter.slice(0, asked);
  }, [inFilter, rotating, page, pageCount, pageSize, onWall, asked]);

  /* Row height on the wall = the room divided by the page's CAPACITY, not by how
     many rows happen to be showing. A short card still compresses rows so every
     player fits, and the height stays put across pages — but filtering down to
     one player no longer stretches that single row to swallow the whole card.
     Spare room is simply left empty below the last row. */
  const wallRowH = (onWall && roomPx > 0) ? Math.max(1, Math.floor(roomPx / Math.max(1, pageSize))) : 0;

  /* Your own row, kept in view when you fall below the cut. */
  const mine = meUserId ? ranked.find((r) => String(r.userId) === String(meUserId)) : null;
  const pinned = shows("pin") && mine && !visible.includes(mine) ? mine : null;

  const cardTitle = title || settings.title || "Leaderboard";
  const style = primaryColor
    ? { "--gmb-primary": primaryColor }
    : undefined;

  const cell = (row, col) => formatAmount(col, row.values?.[col.key]);

  const renderRow = (row, i, isPin) => {
    const rankIdx = ranked.indexOf(row);
    const beats = Number(row.beatCount) || 0;
    const metricN = columns.length || 1;
    /* When qualify is on (e.g. need 4/5), the pill matches that gate — same rule
       that lifts qualified players in the sort. Points-only race is for boards
       with the gate turned off. */
    const rowYetiPts = Number.isFinite(Number(row.yetiPts)) ? Number(row.yetiPts) : yetiPts;
    const aheadOnPts = row.pts >= rowYetiPts;
    const aheadOfYeti = qualifyOn ? isQualified(row) : aheadOnPts;
    let yetiTip = aheadOnPts
      ? "Player is ahead of the Yeti on points"
      : "Player is behind the Yeti on points";
    if (qualifyOn) {
      yetiTip = aheadOfYeti
        ? `Qualified — ${beats} of ${metricN} metrics beating the Yeti (need ${qualifyNeed})`
        : `Not qualified — ${beats} of ${metricN} metrics beating the Yeti (need ${qualifyNeed})`;
    }
    return (
      <tr key={row.userId} className={`${!isPin && i === 0 && shows("lead") ? "lead" : ""}${isPin ? " pinme" : ""}`}>
        {shows("rank") && (
          <td className="stick s0">
            <span className={`lbt-rk${isPin ? "" : rankClass(rankIdx, shows("medals"))}`}>{rankIdx + 1}</span>
          </td>
        )}
        {shows("move") && <td className="stick s1">{movement(row.wasRank, rankIdx + 1)}</td>}
        <td className="stick s2 nm">
          <span className="lbt-rep">
            {shows("avatar") && <Avatar row={row} />}
            <span className="lbt-nm">{nameOf(row)}</span>
            {isPin && <span className="lbt-you">you</span>}
          </span>
        </td>
        {showTeam && <td><span className="lbt-team">{wsName[String(row.wsId)] || "—"}</span></td>}
        {showMetrics && columns.map((c) => (
          <td
            className={`num${(row.values?.[c.key] || 0) === 0 ? " zero" : ""}${row.beats?.[c.key] ? " beat" : ""}`}
            key={c.key}
          >
            {cell(row, c)}
          </td>
        ))}
        {shows("yeti") && (
          <td>
            <span
              className={`lbt-yeti ${aheadOfYeti ? "beat" : "chase"}`}
              title={yetiTip}
            >
              Beating the Yeti
              {qualifyOn ? ` · ${beats}/${metricN}` : ""}
            </span>
          </td>
        )}
      </tr>
    );
  };

  /* Decorative shimmer placeholders — not interactive controls. */
  /* eslint-disable jsx-a11y/control-has-associated-label */
  const renderSkeletonRow = (i) => (
    <tr key={`skel-${i}`} className="lbt-skel-row" aria-hidden="true">
      {shows("rank") && (
        <td className="stick s0"><div className="gmb-skel lbt-skel-rk" /></td>
      )}
      {shows("move") && (
        <td className="stick s1"><div className="gmb-skel lbt-skel-mv" /></td>
      )}
      <td className="stick s2 nm">
        <span className="lbt-rep">
          {shows("avatar") && <div className="gmb-skel lbt-skel-av" />}
          <div className="gmb-skel lbt-skel-nm" style={{ width: `${42 + (i % 3) * 10}%` }} />
        </span>
      </td>
      {showTeam && <td><div className="gmb-skel lbt-skel-team" /></td>}
      {showMetrics && columns.map((c) => (
        <td className="num" key={c.key}><div className="gmb-skel lbt-skel-num" /></td>
      ))}
      {shows("yeti") && <td><div className="gmb-skel lbt-skel-yeti" /></td>}
    </tr>
  );
  /* eslint-enable jsx-a11y/control-has-associated-label */

  const colCount =
    1 + (shows("rank") ? 1 : 0) + (shows("move") ? 1 : 0) + (showTeam ? 1 : 0)
    + (showMetrics ? columns.length : 0) + (shows("yeti") ? 1 : 0);

  const skeletonCount = Math.max(5, Math.min(8, pageSize || 8));

  return (
    <div
      className={`lbt${rotating ? " rotating" : ""}${wallRowH ? " tvfit" : ""}${refreshing ? " lbt-refreshing" : ""}`}
      style={{ ...style, "--lbt-rowh": wallRowH ? `${wallRowH}px` : undefined }}
      aria-busy={loading || refreshing || undefined}
    >
      <div className="lbt-head">
        <div>
          <h3 className="lbt-ttl">{cardTitle}</h3>
          {showMetrics && !!columns.length && (
            <div className="lbt-sub">
              {periodWindowLabelTitle(bucket, boardCustom)} · {columns.length} metric{columns.length === 1 ? "" : "s"}
              {multi ? ` · ${workspaceIds.length} workspaces` : ""}
              {qualifyOn ? ` · rank needs ${qualifyNeed}/${columns.length} beating Yeti` : ""}
            </div>
          )}
        </div>
        {(loading || refreshing) && (
          <div className="lbt-busy" aria-live="polite">
            <span>{loading ? "Loading…" : "Updating…"}</span>
          </div>
        )}
        {showPills && (
          <div className="lbt-pills">
            <button
              type="button"
              className={`lbt-pill${activeFilter === "all" ? " on" : ""}`}
              onClick={() => setFilter("all")}
            >
              All
            </button>
            {workspaceIds.map((id) => (
              <button
                key={id}
                type="button"
                className={`lbt-pill${activeFilter === id ? " on" : ""}`}
                onClick={() => setFilter(id)}
              >
                {wsName[id] || "Workspace"}
              </button>
            ))}
          </div>
        )}
      </div>

      <div className="lbt-scroll" ref={scrollRef}>
        <table>
          <thead>
            <tr>
              {shows("rank") && <th className="stick s0">#</th>}
              {shows("move") && <th className="stick s1">Move</th>}
              <th className="stick s2 nm">Player</th>
              {showTeam && <th>Team</th>}
              {showMetrics && columns.map((c) => (
                <th
                  key={c.key}
                  className={`num${activeKey === c.key ? " sorted" : ""}`}
                  data-sort={c.key}
                  onClick={() => setSortKey(c.key)}
                >
                  {c.label}
                  {activeKey === c.key && <span className="ar">{dir < 0 ? "▼" : "▲"}</span>}
                </th>
              ))}
              {shows("yeti") && <th>vs Yeti</th>}
            </tr>
          </thead>
          <tbody>
            {(() => {
              if (loading) {
                return Array.from({ length: skeletonCount }, (_, i) => renderSkeletonRow(i));
              }
              if (visible.length) {
                return (
                  <>
                    {visible.map((row, i) => renderRow(row, i, false))}
                    {pinned && renderRow(pinned, -1, true)}
                  </>
                );
              }
              return (
                <tr>
                  <td colSpan={Math.max(colCount, 1)}>
                    <div className="lbt-empty">
                      <b>No one on the board yet</b>
                      Scores land here as soon as the team logs work this period.
                    </div>
                  </td>
                </tr>
              );
            })()}
          </tbody>
        </table>
      </div>
      {rotating && (
        /* Countdown to the next rotation — fills left-to-right over the interval
           and restarts each page (keyed by page). Shows there is more to come. */
        <div className="lbt-rotbar" aria-hidden="true">
          <i key={page} style={{ animationDuration: `${rotateMs}ms` }} />
          <span className="lbt-rotpg">{(page % pageCount) + 1}/{pageCount}</span>
        </div>
      )}
    </div>
  );
};

LeaderboardTable.propTypes = {
  /* The card's own settings blob — free-form by design, the same shape every
     other widget stores. */
  // eslint-disable-next-line react/forbid-prop-types
  settings: PropTypes.object,
  periodIdx: PropTypes.number,
  boardWindow: PropTypes.string,
  // eslint-disable-next-line react/forbid-prop-types
  boardCustom: PropTypes.object,
  title: PropTypes.string,
  meUserId: PropTypes.string,
};

LeaderboardTable.defaultProps = {
  settings: {},
  periodIdx: 3,
  boardWindow: null,
  boardCustom: null,
  title: undefined,
  meUserId: null,
};

export default LeaderboardTable;
